/**
 * Offline mutation queue.
 *
 * Every driver action (status transition, proof upload, exception, notification
 * read) is written to IndexedDB FIRST, then flushed to the API. That ordering
 * is what makes "never lose a submitted proof of delivery" true: a lost
 * connection, a killed tab or a dead battery cannot drop the submission.
 *
 * - auto-retry on the `online` event, on window focus, and on an
 *   exponential-backoff timer (10s ??? 300s)
 * - server rejections (4xx) are surfaced as *conflicts* ??? the item is kept for
 *   review instead of being retried forever
 * - a status transition rejected with INVALID_TRANSITION is reconciled against
 *   the server: if the job already reached the intended state the item is
 *   treated as succeeded (safe replay)
 * - dependent items (proof ??? completed) chain via `dependsOn` and read the
 *   predecessor response with `{{pred:<field>}}` placeholders
 */
import { API_PATHS, type JobStatus } from '@uroute/shared';
import { useSyncExternalStore } from 'react';
import {
  ApiError,
  NetworkError,
  SessionExpiredError,
  api,
  refreshSession,
  tokenStore,
} from './api';
import { isDelivered } from './jobStatus';
import { invalidate } from './useQuery';

export type QueueKind =
  | 'status'
  | 'acknowledge'
  | 'exception'
  | 'proof'
  | 'notification-read'
  | 'notification-read-all';

export type QueueItemState = 'pending' | 'failed' | 'done';

export interface QueueFile {
  field: string;
  name: string;
  type: string;
  blob: Blob;
}

export interface QueueItem {
  id: string;
  kind: QueueKind;
  /** Human label for the sync sheet, e.g. "Deliver SHIP-1004". */
  label: string;
  userId?: string;
  jobId?: string;
  path: string;
  json?: unknown;
  fields?: Record<string, string>;
  files?: QueueFile[];
  /** Run only after this item has succeeded. */
  dependsOn?: string;
  createdAt: number;
  attempts: number;
  nextAt: number;
  state: QueueItemState;
  error?: string;
  conflict?: boolean;
  response?: unknown;
}

export interface QueueSnapshot {
  items: QueueItem[];
  pending: number;
  failed: number;
  syncing: boolean;
  available: boolean;
}

const DB_NAME = 'uroute-driver';
const STORE = 'queue';
const DB_VERSION = 1;
const BACKOFF_BASE_MS = 10_000;
const BACKOFF_MAX_MS = 300_000;

let queueUser: string | null = null;
let available = true;
let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
  return dbPromise;
}

function idb<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
      }),
  );
}

async function loadItems(): Promise<QueueItem[]> {
  const all = await idb<QueueItem[]>('readonly', (s) => s.getAll() as IDBRequest<QueueItem[]>);
  return [...all].sort((a, b) => a.createdAt - b.createdAt);
}

async function putItem(item: QueueItem): Promise<void> {
  await idb('readwrite', (s) => s.put(item) as IDBRequest<IDBValidKey>);
}

async function deleteItem(id: string): Promise<void> {
  await idb('readwrite', (s) => s.delete(id) as IDBRequest<undefined>);
}

/* ------------------------------ subscriptions ----------------------------- */

let snapshot: QueueSnapshot = {
  items: [],
  pending: 0,
  failed: 0,
  syncing: false,
  available: true,
};
const listeners = new Set<() => void>();

export function getQueueSnapshot(): QueueSnapshot {
  return snapshot;
}

function subscribeQueue(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useQueue(): QueueSnapshot {
  // useSyncExternalStore keeps badge counts consistent across components.
  return useSyncExternalStore(subscribeQueue, getQueueSnapshot, getQueueSnapshot);
}

function emit(next: Partial<QueueSnapshot>): void {
  const items = next.items ?? snapshot.items;
  snapshot = {
    ...snapshot,
    ...next,
    items,
    pending: next.pending ?? items.filter((i) => i.state === 'pending').length,
    failed: next.failed ?? items.filter((i) => i.state === 'failed').length,
    available: next.available ?? available,
  };
  for (const fn of listeners) fn();
}

async function publish(): Promise<void> {
  try {
    const items = await loadItems();
    emit({ items });
  } catch {
    available = false;
    emit({ available: false, items: [] });
  }
}

/* ------------------------------ flush events ------------------------------ */

export interface FlushEvent {
  itemId: string;
  state: QueueItemState;
  label: string;
}

const flushListeners = new Set<(event: FlushEvent) => void>();

/** Progress feed for long submissions (multi-photo proof of delivery). */
export function onFlushEvent(fn: (event: FlushEvent) => void): () => void {
  flushListeners.add(fn);
  return () => {
    flushListeners.delete(fn);
  };
}

function emitFlush(event: FlushEvent): void {
  for (const fn of flushListeners) fn(event);
}
/* --------------------------------- build ---------------------------------- */

export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export interface NewQueueItem {
  kind: QueueKind;
  label: string;
  path: string;
  jobId?: string;
  json?: unknown;
  fields?: Record<string, string>;
  files?: QueueFile[];
  dependsOn?: string;
}

export function makeItem(item: NewQueueItem): QueueItem {
  return {
    id: newId(),
    kind: item.kind,
    label: item.label,
    userId: queueUser ?? undefined,
    jobId: item.jobId,
    path: item.path,
    json: item.json,
    fields: item.fields,
    files: item.files,
    dependsOn: item.dependsOn,
    createdAt: Date.now(),
    attempts: 0,
    nextAt: 0,
    state: 'pending',
  };
}

/** Which driver owns queued work ??? never replay another account's actions. */
export function setQueueUser(userId: string | null): void {
  queueUser = userId;
}

export async function enqueue(items: QueueItem[]): Promise<void> {
  try {
    for (const item of items) await putItem(item);
  } catch (err) {
    available = false;
    emit({ available: false });
    throw new Error(
      'This device cannot save work offline (private browsing?). Reconnect and try again.',
      { cause: err },
    );
  }
  await publish();
}

/* -------------------------------- flush ----------------------------------- */

function backoff(attempts: number): number {
  return Math.min(BACKOFF_BASE_MS * 2 ** Math.min(attempts, 5), BACKOFF_MAX_MS);
}

function interpolate(value: unknown, predecessor: unknown): unknown {
  if (typeof value === 'string') {
    return value.replace(/\{\{pred:([\w.]+)\}\}/g, (_m, path: string) => {
      const parts = path.split('.');
      let cur: unknown = predecessor;
      for (const p of parts) {
        if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
          cur = (cur as Record<string, unknown>)[p];
        } else {
          return '';
        }
      }
      return cur === null || cur === undefined ? '' : String(cur);
    });
  }
  if (Array.isArray(value)) return value.map((v) => interpolate(v, predecessor));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, interpolate(v, predecessor)]),
    );
  }
  return value;
}

async function sendItem(item: QueueItem, predecessor: QueueItem | undefined): Promise<unknown> {
  const body = interpolate(item.json, predecessor?.response);
  const hasMultipart = Boolean(item.files?.length) || Boolean(item.fields);

  const doSend = async (): Promise<Response> => {
    const headers = new Headers();
    const token = tokenStore.access();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    let init: RequestInit = { method: 'POST', headers };
    if (hasMultipart) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(item.fields ?? {})) fd.append(k, v);
      for (const f of item.files ?? []) fd.append(f.field, f.blob, f.name);
      init = { ...init, body: fd };
    } else if (body !== undefined && body !== null) {
      headers.set('Content-Type', 'application/json');
      init = { ...init, body: JSON.stringify(body) };
    }
    try {
      return await fetch(item.path, init);
    } catch {
      throw new NetworkError();
    }
  };

  let res = await doSend();
  if (res.status === 401) {
    const refreshed = await refreshSession();
    if (!refreshed) throw new SessionExpiredError();
    res = await doSend();
  }

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const envelope = (parsed as { error?: import('@uroute/shared').ApiErrorBody } | null)?.error;
    throw new ApiError(res.status, envelope ?? { code: 'INTERNAL_ERROR', message: res.statusText });
  }
  return parsed;
}

/** A rejected transition may simply mean the job already moved — verify. */
async function reconcileStatus(item: QueueItem): Promise<boolean> {
  const to = (item.json as { to?: string } | undefined)?.to;
  if (!item.jobId || !to) return false;
  try {
    /* Cache-bust: the service worker serves the stale job body first, and a
       reconciliation right after approval must compare against server truth. */
    const job = await api.get<{ status: string }>(`${API_PATHS.jobs}/${item.jobId}?_=${Date.now()}`);
    if (job.status === to) return true;
    /* Completion auto-advances (completed -> invoice_draft): any delivered state counts. */
    if (to === 'completed') return isDelivered(job.status as JobStatus);
    return false;
  } catch {
    return false;
  }
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof SessionExpiredError) return err.message;
  return 'Sync failed.';
}

let flushInFlight: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

function bindListeners(): void {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  window.addEventListener('online', () => void flushQueue());
  window.addEventListener('focus', () => void flushQueue());
}

export function flushQueue(): Promise<void> {
  bindListeners();
  flushInFlight ??= runFlush().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

async function runFlush(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    await publish();
    return;
  }
  emit({ syncing: true });
  try {
    const items = await loadItems();
    const byId = new Map(items.map((i) => [i.id, i]));

    for (const item of items) {
      if (item.state !== 'pending') continue;
      if (item.nextAt > Date.now()) continue;
      if (queueUser && item.userId && item.userId !== queueUser) continue;

      if (item.dependsOn) {
        const pred = byId.get(item.dependsOn);
        if (!pred) {
          item.state = 'failed';
          item.error = 'A required step is missing.';
          item.conflict = true;
          await putItem(item);
          continue;
        }
        if (pred.state === 'failed') {
          item.state = 'failed';
          item.error = `Blocked by a failed step: ${pred.label}`;
          item.conflict = true;
          await putItem(item);
          continue;
        }
        if (pred.state !== 'done') continue;
      }

      const predecessor = item.dependsOn ? byId.get(item.dependsOn) : undefined;
      try {
        const response = await sendItem(item, predecessor);
        item.state = 'done';
        item.error = undefined;
        item.conflict = undefined;
        item.response = response;
        await putItem(item);
        byId.set(item.id, item);
        emitFlush({ itemId: item.id, state: 'done', label: item.label });
        /* The UI must reflect work that finally reached the server — a delivery
           completed from the offline queue has to flip the detail page and the
           today list, not just the server. */
        if (item.jobId) {
          invalidate(`job/${item.jobId}`);
          invalidate('me/jobs');
        }
      } catch (err) {
        if (err instanceof NetworkError) {
          item.attempts += 1;
          item.nextAt = Date.now() + backoff(item.attempts);
          await putItem(item);
          break; // connection dropped ??? the timer/online event will resume
        }
        if (err instanceof SessionExpiredError) {
          item.attempts += 1;
          item.nextAt = Date.now() + 60_000;
          await putItem(item);
          break; // driver must sign in again; work stays queued
        }
        if (err instanceof ApiError && err.status >= 500) {
          item.attempts += 1;
          item.nextAt = Date.now() + backoff(item.attempts);
          await putItem(item);
          break; // server trouble ??? back off rather than hammer it
        }
        if (item.kind === 'status' && (await reconcileStatus(item))) {
          item.state = 'done';
          item.error = undefined;
          item.response = { reconciled: true };
          await putItem(item);
          byId.set(item.id, item);
          continue;
        }
        item.state = 'failed';
        item.conflict = true;
        item.error = friendlyError(err);
        await putItem(item);
        byId.set(item.id, item);
        emitFlush({ itemId: item.id, state: 'failed', label: item.label });
      }
    }

    /* Drop completed items no longer needed by a dependent. FAILED dependents
       are kept too: retrying them needs the predecessor response (e.g. the
       proof id interpolated into the completion request). */
    const waiting = new Set(
      (await loadItems())
        .filter((i) => (i.state === 'pending' || i.state === 'failed') && i.dependsOn)
        .map((i) => i.dependsOn),
    );
    for (const item of items) {
      if (item.state === 'done' && !waiting.has(item.id)) await deleteItem(item.id);
    }
  } finally {
    emit({ syncing: false });
    await publish();
    scheduleRetry();
  }
}

function scheduleRetry(): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  const now = Date.now();
  const next = snapshot.items
    .filter((i) => i.state === 'pending')
    .reduce<number | null>((min, i) => (min === null ? i.nextAt : Math.min(min, i.nextAt)), null);
  if (next === null) return;
  const delay = Math.max(next - now, 1_000);
  retryTimer = setTimeout(() => void flushQueue(), delay);
}

/* ------------------------------ public helpers ---------------------------- */

export type SubmitResult = { status: 'done' } | { status: 'queued' } | { status: 'failed'; error: string };

/**
 * Persist the chain, then (when online) push it and report the outcome.
 * Offline ??? `{ status: 'queued' }` and the work syncs itself later.
 */
export async function submitItems(
  items: QueueItem[],
  onProgress?: (settled: number, total: number, label: string) => void,
): Promise<SubmitResult> {
  await enqueue(items);
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { status: 'queued' };
  const ownIds = new Set(items.map((i) => i.id));
  let settled = 0;
  const off = onProgress
    ? onFlushEvent((event) => {
        if (!ownIds.has(event.itemId)) return;
        settled += 1;
        onProgress(Math.min(settled, items.length), items.length, event.label);
      })
    : null;
  try {
    await flushQueue();
    /* flushQueue may have joined a flush that loaded its item list before our
       enqueue landed — keep flushing until our items are settled or backing
       off, so a concurrent flush can never silently swallow a submission. */
    for (let i = 0; i < 3; i++) {
      const mid = await loadItems();
      const own = items.map((it) => mid.find((x) => x.id === it.id) ?? null);
      const ready = own.some((it) => it !== null && it.state === 'pending' && it.nextAt <= Date.now());
      if (!ready) break;
      await flushQueue();
    }
  } finally {
    off?.();
  }
  const stored = await loadItems();
  const states = items.map((i) => stored.find((x) => x.id === i.id) ?? null);
  const failed = states.find((s) => s?.state === 'failed');
  if (failed?.error) return { status: 'failed', error: failed.error };
  /* runFlush deletes items the moment they succeed ??? a missing entry is done,
     not pending. Only items still in the store and pending are unsent. */
  if (states.some((s) => s !== null && s.state === 'pending')) return { status: 'queued' };
  return { status: 'done' };
}

/** Reconcile one conflicted item (Retry in the sync sheet). */
export async function retryItem(id: string): Promise<void> {
  try {
    const item = await idb<QueueItem | undefined>('readonly', (s) => s.get(id) as IDBRequest<QueueItem | undefined>);
    if (!item) return;
    item.state = 'pending';
    item.conflict = undefined;
    item.error = undefined;
    item.attempts = 0;
    item.nextAt = 0;
    await putItem(item);
  } catch {
    /* ignore */
  }
  await flushQueue();
}

export async function discardItem(id: string): Promise<void> {
  await deleteItem(id);
  await publish();
}

export async function retryAll(): Promise<void> {
  try {
    const items = await loadItems();
    for (const item of items) {
      if (item.state !== 'failed') continue;
      item.state = 'pending';
      item.conflict = undefined;
      item.error = undefined;
      item.attempts = 0;
      item.nextAt = 0;
      await putItem(item);
    }
  } catch {
    /* ignore */
  }
  await flushQueue();
}

/** Kick the queue once the driver is signed in. */
export function startQueue(): void {
  bindListeners();
  void publish();
  void flushQueue();
}
